import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Biz360Logo } from "../components/layout/Biz360Logo";
import { PublicFooter } from "../components/layout/PublicFooter";

export const MentionsLegalesCGUPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Biz360Logo />
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            data-testid="back-home-btn"
          >
            <ArrowLeft size={16} /> Retour
          </button>
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-6 py-10" data-testid="legal-cgu-page">
        <h1 className="text-3xl font-bold mb-2" style={{ color: "#210875" }}>
          Mentions légales & Conditions Générales d'Utilisation
        </h1>
        <p className="text-sm text-muted-foreground mb-8">Dernière mise à jour : 8 mai 2026</p>

        {/* ============== MENTIONS LEGALES ============== */}
        <h2
          className="text-2xl font-bold mb-4 pb-2 border-b"
          style={{ color: "#210875", borderColor: "#210875" }}
        >
          Partie A — Mentions légales
        </h2>

        <Section title="1. Éditeur du site">
          <p>
            Le site <strong>biz360.obusiness.fr</strong> (ci-après « Biz360 ») est édité par :
          </p>
          <ul>
            <li>
              <strong>Tiphaine SAMBLAT</strong> — Entrepreneur individuel (auto-entrepreneur)
            </li>
            <li>
              <strong>SIREN :</strong> 913 345 682
            </li>
            <li>
              <strong>SIRET (siège) :</strong> 913 345 682 00037
            </li>
            <li>
              <strong>Code APE / NAF :</strong> 82.99Z — Autres activités de soutien aux entreprises
              n.c.a.
            </li>
            <li>
              <strong>Adresse du siège :</strong> 48 C Boulevard de la Méditerranée, 31270
              FROUZINS — France
            </li>
            <li>
              <strong>Date de création :</strong> 1ᵉʳ juin 2022
            </li>
            <li>
              <strong>Email :</strong>{" "}
              <a
                href="mailto:t.samblat@gmail.com"
                className="underline"
                style={{ color: "#210875" }}
              >
                t.samblat@gmail.com
              </a>
            </li>
            <li>
              <strong>TVA intracommunautaire :</strong> Non assujettie (régime de la franchise en
              base de TVA — art. 293 B du CGI)
            </li>
          </ul>
        </Section>

        <Section title="2. Directrice de la publication">
          <p>Tiphaine SAMBLAT, en qualité d'éditrice individuelle.</p>
        </Section>

        <Section title="3. Hébergement & nom de domaine">
          <p>
            <strong>Hébergement applicatif :</strong> Le site et l'application sont hébergés par{" "}
            <strong>Emergent</strong> (infrastructure cloud Kubernetes managée — Union Européenne).
          </p>
          <p>
            <strong>Base de données :</strong> opérée via <strong>MongoDB Atlas</strong> (région
            Union Européenne).
          </p>
          <p>
            <strong>Bureau d'enregistrement du nom de domaine :</strong>{" "}
            <a
              href="https://www.ovh.com"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
              style={{ color: "#210875" }}
            >
              OVH SAS
            </a>{" "}
            — 2 rue Kellermann, 59100 Roubaix, France — RCS Lille Métropole 424 761 419.
          </p>
        </Section>

        <Section title="4. Propriété intellectuelle">
          <p>
            L'ensemble des éléments composant le site Biz360 (marque, logo, design, textes,
            graphismes, code source, structure) sont la propriété exclusive de l'éditrice et
            protégés par le Code de la propriété intellectuelle.
          </p>
          <p>
            Toute reproduction, représentation, modification, publication, transmission, ou
            exploitation totale ou partielle, sans autorisation expresse écrite de l'éditrice, est
            strictement interdite et constitutive de contrefaçon (articles L.335-2 et suivants du
            CPI).
          </p>
        </Section>

        <Section title="5. Données personnelles">
          <p>
            Le traitement des données personnelles est régi par notre{" "}
            <a
              href="/politique-confidentialite"
              className="underline"
              style={{ color: "#210875" }}
            >
              Politique de confidentialité
            </a>
            , conforme au RGPD (UE 2016/679) et à la loi Informatique et Libertés.
          </p>
        </Section>

        <Section title="6. Crédits">
          <p>Conception, développement & édition : Tiphaine SAMBLAT.</p>
          <p>
            Pour toute question ou signalement d'un contenu illicite (LCEN, art. 6) :{" "}
            <a
              href="mailto:t.samblat@gmail.com"
              className="underline"
              style={{ color: "#210875" }}
            >
              t.samblat@gmail.com
            </a>
            .
          </p>
        </Section>

        {/* ============== CGU ============== */}
        <h2
          className="text-2xl font-bold mb-4 mt-12 pb-2 border-b"
          style={{ color: "#210875", borderColor: "#210875" }}
        >
          Partie B — Conditions Générales d'Utilisation
        </h2>

        <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
          Les présentes Conditions Générales d'Utilisation (CGU) régissent l'accès et l'utilisation
          de l'application Biz360, accessible à l'adresse <strong>biz360.obusiness.fr</strong>. En
          s'inscrivant ou en utilisant l'application, l'utilisateur accepte sans réserve les
          présentes CGU.
        </p>

        <Section title="Article 1 — Présentation de l'application">
          <p>
            Biz360 est une application web de pilotage de gestion dédiée aux auto-entrepreneurs.
            Elle propose notamment :
          </p>
          <ul>
            <li>Un tableau de bord de suivi de l'activité</li>
            <li>Des alertes Urssaf avec montants précalculés</li>
            <li>Un suivi du seuil de franchise en base de TVA</li>
            <li>Un suivi des ventes et dépenses</li>
            <li>Un module de relances clients</li>
            <li>Une déclaration Urssaf précalculée</li>
          </ul>
          <p>
            Biz360 est édité par Tiphaine SAMBLAT, auto-entrepreneur, SIRET 913 345 682 00037,
            ci-après dénommée « l'Éditeur ».
          </p>
        </Section>

        <Section title="Article 2 — Accès au service">
          <p>
            L'accès à Biz360 est réservé aux personnes physiques majeures exerçant en tant
            qu'auto-entrepreneur ou micro-entrepreneur en France.
          </p>
          <p>L'utilisateur s'engage à :</p>
          <ul>
            <li>Fournir des informations exactes lors de son inscription</li>
            <li>Maintenir la confidentialité de ses identifiants de connexion</li>
            <li>Ne pas partager son compte avec des tiers</li>
          </ul>
        </Section>

        <Section title="Article 3 — Abonnement et tarification">
          <p>
            <strong>3.1 Offres disponibles</strong>
          </p>
          <ul>
            <li>Abonnement mensuel : 12 €/mois</li>
            <li>Abonnement annuel : 97 €/an</li>
            <li>Offres promotionnelles ponctuelles : selon conditions affichées au moment de la souscription</li>
          </ul>
          <p className="text-xs italic">
            ⚖️ TVA non applicable — art. 293 B du CGI (franchise en base de TVA).
          </p>
          <p>
            <strong>3.2 Paiement</strong>
          </p>
          <p>
            Le paiement est effectué en ligne de manière sécurisée via <strong>Stripe</strong>
            . L'Éditeur ne conserve aucune donnée bancaire. En cas d'échec de paiement, l'accès au
            service peut être suspendu.
          </p>
          <p>
            <strong>3.3 Résiliation</strong>
          </p>
          <p>
            L'utilisateur peut résilier son abonnement à tout moment depuis son espace personnel.
            La résiliation prend effet à la fin de la période d'abonnement en cours. Aucun
            remboursement prorata temporis n'est effectué sauf disposition légale contraire.
          </p>
        </Section>

        <Section title="Article 4 — Geste commercial : période de réflexion volontaire">
          <p>
            Le service Biz360 est exclusivement destiné à des <strong>professionnels</strong>{" "}
            agissant dans le cadre de leur activité (auto-entrepreneurs, micro-entrepreneurs,
            entrepreneurs individuels ou toute autre personne exerçant une activité économique
            indépendante). En conséquence, les dispositions du Code de la consommation
            relatives au droit de rétractation des consommateurs <strong>ne s'appliquent
            pas</strong> aux abonnements souscrits auprès de Biz360. Toute souscription est
            ferme et définitive à compter de la validation du paiement.
          </p>
          <p>
            <strong>Engagement commercial volontaire de l'Éditeur :</strong> à titre purement
            commercial, sans constituer une obligation légale ni un précédent et sous réserve
            du respect des conditions ci-dessous, l'Éditeur accorde une <strong>période de
            réflexion de 14 jours</strong> à compter de la souscription, pendant laquelle
            l'utilisateur peut demander le remboursement intégral de son abonnement.
          </p>
          <p>
            Cette faculté commerciale est soumise aux conditions cumulatives suivantes : (i)
            la demande doit être formulée par écrit à{" "}
            <a href="mailto:t.samblat@gmail.com" className="underline" style={{ color: "#210875" }}>
              t.samblat@gmail.com
            </a>{" "}
            dans un délai maximum de 14 jours suivant la souscription ; (ii) l'utilisateur ne
            doit pas avoir fait un usage répété ou intensif du service ; (iii) l'Éditeur
            apprécie chaque demande au cas par cas et se réserve la possibilité de la refuser
            en cas d'abus manifeste. L'Éditeur peut, à tout moment et sans préavis, modifier
            ou supprimer ce geste commercial pour les souscriptions futures.
          </p>
        </Section>

        <Section title="Article 5 — Obligations et responsabilité de l'utilisateur">
          <p>
            L'utilisateur s'engage à utiliser Biz360 dans le respect des lois et règlements en
            vigueur, et notamment à :
          </p>
          <ul>
            <li>Ne pas utiliser l'application à des fins illicites ou frauduleuses</li>
            <li>
              Ne pas tenter d'accéder sans autorisation aux systèmes ou données de l'application
            </li>
            <li>Ne pas perturber le fonctionnement normal de l'application</li>
            <li>Saisir des données exactes et à jour concernant son activité professionnelle</li>
          </ul>
          <p>
            L'utilisateur est seul responsable des données qu'il saisit dans l'application et de
            l'utilisation qu'il fait des informations fournies par Biz360.
          </p>
        </Section>

        <Section title="Article 6 — Responsabilité de l'Éditeur">
          <p>
            <strong>6.1 Nature des informations fournies</strong>
          </p>
          <p>
            Biz360 est un outil d'aide à la gestion. Les informations et calculs fournis
            (cotisations Urssaf estimées, seuils TVA, etc.) sont donnés à titre indicatif et ne
            constituent pas un conseil juridique, fiscal ou comptable. L'utilisateur reste seul
            responsable de ses déclarations officielles auprès de l'Urssaf, de l'administration
            fiscale et de tout autre organisme.
          </p>
          <p>
            <strong>6.2 Disponibilité du service</strong>
          </p>
          <p>
            L'Éditeur s'engage à faire ses meilleurs efforts pour assurer la disponibilité de
            l'application 24h/24 et 7j/7. Toutefois, des interruptions ponctuelles peuvent
            survenir pour maintenance ou en cas de force majeure. L'Éditeur ne saurait être tenu
            responsable des dommages résultant d'une indisponibilité temporaire du service.
          </p>
          <p>
            <strong>6.3 Limitation de responsabilité</strong>
          </p>
          <p>
            La responsabilité de l'Éditeur ne peut être engagée en cas de perte de données due à
            un comportement de l'utilisateur, de dommages indirects, de perte de chiffre
            d'affaires ou de manque à gagner liés à l'utilisation ou à la non-disponibilité de
            l'application.
          </p>
        </Section>

        <Section title="Article 7 — Propriété intellectuelle">
          <p>
            L'ensemble des éléments constituant l'application Biz360 (design, logo, textes,
            fonctionnalités, code source) sont la propriété exclusive de l'Éditeur et sont
            protégés par les lois françaises et internationales relatives à la propriété
            intellectuelle. Toute reproduction, représentation, modification ou exploitation non
            autorisée est strictement interdite.
          </p>
        </Section>

        <Section title="Article 8 — Données personnelles">
          <p>
            Le traitement des données personnelles est régi par la{" "}
            <a
              href="/politique-confidentialite"
              className="underline"
              style={{ color: "#210875" }}
            >
              Politique de Confidentialité
            </a>{" "}
            de Biz360. Cette politique est incorporée aux présentes CGU par référence.
          </p>
        </Section>

        <Section title="Article 9 — Suspension et résiliation du compte">
          <p>
            L'Éditeur se réserve le droit de suspendre ou résilier le compte d'un utilisateur sans
            préavis en cas de :
          </p>
          <ul>
            <li>Non-paiement de l'abonnement</li>
            <li>Violation des présentes CGU</li>
            <li>Utilisation frauduleuse ou abusive de l'application</li>
          </ul>
        </Section>

        <Section title="Article 10 — Modification des CGU">
          <p>
            L'Éditeur se réserve le droit de modifier les présentes CGU à tout moment. Les
            utilisateurs seront informés de toute modification substantielle par e-mail ou
            notification dans l'application, au moins 30 jours avant l'entrée en vigueur des
            nouvelles conditions. La poursuite de l'utilisation de l'application après cette date
            vaut acceptation des nouvelles CGU.
          </p>
        </Section>

        <Section title="Article 11 — Droit applicable et juridiction compétente">
          <p>
            Les présentes CGU sont soumises au droit français. Le service Biz360 est
            exclusivement destiné aux professionnels (auto-entrepreneurs et
            micro-entrepreneurs) agissant dans le cadre de leur activité professionnelle. En
            cas de litige relatif à l'interprétation ou à l'exécution des présentes CGU, et à
            défaut de résolution amiable, <strong>compétence expresse est attribuée aux
            tribunaux du ressort de Toulouse</strong>, sous réserve des dispositions légales
            impératives applicables.
          </p>
        </Section>

        <Section title="Article 12 — Contact">
          <p>
            Pour toute question relative aux présentes CGU, vous pouvez contacter l'Éditeur à
            l'adresse suivante :{" "}
            <a
              href="mailto:t.samblat@gmail.com"
              className="underline"
              style={{ color: "#210875" }}
            >
              t.samblat@gmail.com
            </a>
            .
          </p>
        </Section>
      </main>

      <PublicFooter />
    </div>
  );
};

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h3 className="text-lg font-bold mb-3" style={{ color: "#210875" }}>
      {title}
    </h3>
    <div className="text-sm text-muted-foreground space-y-2 leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1">
      {children}
    </div>
  </section>
);

export default MentionsLegalesCGUPage;
